import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";

import type { GrantRadarInterest, GrantRadarProfilePayload, GrantRadarState } from "@/api/grant_radar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const SIZE_LABELS: Record<string, string> = {
  under_100k: "Under $100,000",
  from_100k_to_1m: "$100,000 to $1 million",
  from_1m_to_5m: "$1 million to $5 million",
  over_5m: "Over $5 million",
  unknown: "Not sure yet",
};

const TIMING_LABELS: Record<string, string> = {
  within_12_months: "Within 12 months",
  one_to_three_years: "In 1 to 3 years",
  longer_term: "Longer term",
  unknown: "Not sure yet",
};

const TEXT_FIELDS: { key: keyof GrantRadarProfilePayload; label: string; hint: string }[] = [
  { key: "readiness", label: "Project readiness", hint: "Plans, designs, right-of-way, permits or community input already done." },
  { key: "match_capacity", label: "Matching funds", hint: "How much local match you can commit, if any." },
  { key: "exclusions", label: "Not interested in", hint: "Funders or program types to skip. Grant Radar still checks eligibility itself." },
  { key: "previous_awards", label: "Previous awards", hint: "Grants you have received, with funder and year." },
  { key: "preferred_sources", label: "Funders to watch", hint: "Funder names or program page links to check every time." },
];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: GrantRadarState;
  saving: boolean;
  error: string | null;
  onSave: (payload: GrantRadarProfilePayload) => void;
};

function initialState(data: GrantRadarState): GrantRadarProfilePayload {
  const profile = data.profile;
  return {
    applicant_name: profile?.applicant_name ?? "",
    community_details: profile?.community_details ?? "",
    funding_needs: profile?.funding_needs ?? "",
    interests: profile?.interests?.length ? profile.interests : [{ name: "", details: "", active: true }],
    project_size: profile?.project_size ?? "",
    project_timing: profile?.project_timing ?? "",
    readiness: profile?.readiness ?? "",
    match_capacity: profile?.match_capacity ?? "",
    exclusions: profile?.exclusions ?? "",
    previous_awards: profile?.previous_awards ?? "",
    preferred_sources: profile?.preferred_sources ?? "",
  };
}

export function GrantRadarProfileDialog({ open, onOpenChange, data, saving, error, onSave }: Props) {
  const [form, setForm] = useState<GrantRadarProfilePayload>(() => initialState(data));

  useEffect(() => {
    if (open) setForm(initialState(data));
    // Reset only when the dialog opens, so a background refetch never wipes unsaved edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const interests = form.interests ?? [];
  const setInterest = (index: number, change: Partial<GrantRadarInterest>) =>
    setForm((current) => ({
      ...current,
      interests: (current.interests ?? []).map((interest, i) => (i === index ? { ...interest, ...change } : interest)),
    }));
  const hasActive = interests.some((interest) => interest.active && interest.name.trim());
  const hasApplicant = Boolean(form.applicant_name?.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Your Grant Radar profile</DialogTitle>
          <DialogDescription>Only you see this profile. Grant Radar searches for each active interest.</DialogDescription>
        </DialogHeader>

        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            onSave({ ...form, interests: interests.filter((interest) => interest.name.trim()) });
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="radar-applicant">Applying as (required)</Label>
            <p className="text-xs text-muted-foreground">The department or office you apply for, such as Pea Ridge Fire Department.</p>
            <Input
              id="radar-applicant"
              required
              maxLength={200}
              value={form.applicant_name ?? ""}
              onChange={(event) => setForm({ ...form, applicant_name: event.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="radar-community">About you and your community</Label>
            <p className="text-xs text-muted-foreground">
              Funders often limit eligibility by facts like these: population, median household income or low-to-moderate income status, county and
              area you serve, staff or volunteer count, and registrations such as SAM.gov. Grant Radar only uses what you write here; it does not look
              these up, so anything left out is shown as something to confirm.
            </p>
            <Textarea
              id="radar-community"
              rows={4}
              maxLength={2000}
              placeholder="For example: Population 6,500 (2020 Census). We serve the city and a surrounding rural district. Registered in SAM.gov."
              value={form.community_details ?? ""}
              onChange={(event) => setForm({ ...form, community_details: event.target.value })}
            />
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">What you need funding for (required)</legend>
            <p className="text-xs text-muted-foreground">For example: brush trucks, a new fire station, or trail connections.</p>
            {interests.map((interest, index) => (
              <div key={interest.id ?? `new-${index}`} className="space-y-2 rounded-md border p-3">
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={`Interest ${index + 1} name`}
                    placeholder="Project or need"
                    value={interest.name}
                    maxLength={120}
                    onChange={(event) => setInterest(index, { name: event.target.value })}
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`interest-active-${index}`}
                      aria-label={`Search for ${interest.name.trim() || `interest ${index + 1}`}`}
                      checked={interest.active}
                      onCheckedChange={(checked) => setInterest(index, { active: checked })}
                    />
                    <Label htmlFor={`interest-active-${index}`} className="w-14 text-xs">
                      {interest.active ? "Active" : "Paused"}
                    </Label>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${interest.name || "interest"}`}
                    onClick={() => setForm((current) => ({ ...current, interests: interests.filter((_, i) => i !== index) }))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea
                  aria-label={`Interest ${index + 1} details`}
                  placeholder="Details that help match funders"
                  rows={2}
                  value={interest.details ?? ""}
                  onChange={(event) => setInterest(index, { details: event.target.value })}
                />
              </div>
            ))}
            {interests.length < 12 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setForm((current) => ({ ...current, interests: [...interests, { name: "", details: "", active: true }] }))}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add interest
              </Button>
            ) : null}
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="radar-needs">Overall context</Label>
            <Textarea
              id="radar-needs"
              rows={3}
              value={form.funding_needs ?? ""}
              onChange={(event) => setForm({ ...form, funding_needs: event.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="radar-size">Typical project size</Label>
              <Select value={form.project_size || undefined} onValueChange={(value) => setForm({ ...form, project_size: value })}>
                <SelectTrigger id="radar-size">
                  <SelectValue placeholder="Choose a size" />
                </SelectTrigger>
                <SelectContent>
                  {data.options.project_sizes.map((size) => (
                    <SelectItem key={size} value={size}>
                      {SIZE_LABELS[size] ?? size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="radar-timing">When the project would start</Label>
              <Select value={form.project_timing || undefined} onValueChange={(value) => setForm({ ...form, project_timing: value })}>
                <SelectTrigger id="radar-timing">
                  <SelectValue placeholder="Choose timing" />
                </SelectTrigger>
                <SelectContent>
                  {data.options.project_timings.map((timing) => (
                    <SelectItem key={timing} value={timing}>
                      {TIMING_LABELS[timing] ?? timing}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {TEXT_FIELDS.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label htmlFor={`radar-${field.key}`}>{field.label}</Label>
              <p className="text-xs text-muted-foreground">{field.hint}</p>
              <Textarea
                id={`radar-${field.key}`}
                rows={2}
                value={(form[field.key] as string) ?? ""}
                onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
              />
            </div>
          ))}

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !hasApplicant || !hasActive}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Save profile
            </Button>
          </DialogFooter>
          {!hasApplicant || !hasActive ? (
            <p className="text-right text-xs text-muted-foreground">Add who you are applying as and at least one active interest to save.</p>
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
